/**
 * LazyTabs — React.lazy wrappers for code-split tab components.
 * Provides lazy imports, loading/error fallbacks, and a LazyTabWrapper
 * that combines an ErrorBoundary + Suspense for each lazy tab.
 */

import React, { lazy, Suspense, useState, Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Activity, AlertTriangle, RefreshCw } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError, tryAutoReloadForChunkError } from './ErrorBoundary';

// ─── Lazy Imports ────────────────────────────────────────────

export const LazyLiveStudioTab = lazy(() =>
  import('@components/LiveStudio/LiveStudioTab').then(m => ({ default: m.LiveStudioTab }))
);
export const LazySunoHistoryTab = lazy(() =>
  import('@components/SunoHistoryTab').then(m => ({ default: m.SunoHistoryTab }))
);
export const LazyHHHistoryTab = lazy(() =>
  import('@components/HHHistoryTab').then(m => ({ default: m.HHHistoryTab }))
);
export const LazyMusicCriticTab = lazy(() =>
  import('@components/MusicCriticTab').then(m => ({ default: m.MusicCriticTab }))
);
export const LazyVocalBlender = lazy(() =>
  import('@components/VocalForge/VocalBlender').then(m => ({ default: m.VocalBlender }))
);
export const LazyProductionIntelligence = lazy(() =>
  import('@components/ProductionIntelligence/ProductionIntelligence').then(m => ({ default: m.ProductionIntelligence }))
);
export const LazyAlbumModeContainer = lazy(() =>
  import('@features/AlbumMode/AlbumModeContainer').then(m => ({ default: m.AlbumModeContainer }))
);
export const LazyRiffWriterTab = lazy(() =>
  import('@components/RiffWriter/RiffWriterTab').then(m => ({ default: m.RiffWriterTab }))
);
export const LazyNarrativeEngineTab = lazy(() =>
  import('@components/NarrativeEngine/NarrativeEngineTab').then(m => ({ default: m.NarrativeEngineTab }))
);
export const LazySessionHistoryTab = lazy(() =>
  import('@components/SessionHistory/SessionHistoryTab').then(m => ({ default: m.SessionHistoryTab }))
);
export const LazyMidiForgeTab = lazy(() =>
  import('@components/MidiForge/MidiForgeTab').then(m => ({ default: m.MidiForgeTab }))
);
export const LazyMasterForgeTab = lazy(() =>
  import('@components/MasterForge/MasterForgeTab').then(m => ({ default: m.MasterForgeTab }))
);
export const LazyAnalyticsTab = lazy(() =>
  import('@components/Analytics/AnalyticsTab').then(m => ({ default: m.AnalyticsTab }))
);
export const LazySuiteTab = lazy(() =>
  import('@components/Suite/SuiteTab').then(m => ({ default: m.SuiteTab }))
);
export const LazyAxeLabTab = lazy(() =>
  import('@components/AxeLab/AxeLabTab').then(m => ({ default: m.AxeLabTab }))
);
export const LazyRouletteTab = lazy(() =>
  import('@components/Roulette/RouletteTab').then(m => ({ default: m.RouletteTab }))
);
export const LazySimpleModeTab = lazy(() =>
  import('@components/SimpleMode/SimpleModeTab').then(m => ({ default: m.SimpleModeTab }))
);
export const LazyStudioRouletteTab = lazy(() =>
  import('@components/StudioRoulette/StudioRouletteTab').then(m => ({ default: m.StudioRouletteTab }))
);
export const LazySpeechTab = lazy(() =>
  import('@components/Speech/SpeechTab').then(m => ({ default: m.SpeechTab }))
);
export const LazyManualEntryTab = lazy(() =>
  import('@components/ManualEntryTab').then(m => ({ default: m.ManualEntryTab }))
);

// ─── Loading Fallback ────────────────────────────────────────

export const TabLoadingFallback: React.FC = () => (
  <div className="min-h-[400px] flex flex-col items-center justify-center space-y-6">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-accent/20 border-t-accent rounded-full animate-spin" />
      <Activity size={24} className="absolute inset-0 m-auto text-accent animate-pulse" />
    </div>
    <p className="text-[11px] font-black text-slate-400 uppercase tracking-[0.3em]">
      Loading module...
    </p>
  </div>
);

// ─── Error Fallback ──────────────────────────────────────────

const TabErrorFallback: React.FC<{ error: Error | null; onRetry: () => void }> = ({ error, onRetry }) => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
      <div className="w-12 h-12 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
        <AlertTriangle size={24} className="text-red-500" />
      </div>
      <h3 className="text-lg font-black text-slate-50 mb-2 uppercase tracking-tight">
        Module Load Failed
      </h3>
      <p className="text-sm text-slate-400 mb-4">
        {error?.message || 'Failed to load this tab. Check your connection and try again.'}
      </p>
      {/* First stack frame (file:line:col) -- enough to map back through the
          deployed source maps when reporting a crash. */}
      {error?.stack && (
        <p className="text-[10px] text-slate-600 font-mono mb-4 break-all">
          {error.stack.split('\n').map(l => l.trim()).find(l => l.startsWith('at ')) ?? ''}
        </p>
      )}
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-slate-950 rounded-lg font-bold text-xs uppercase tracking-widest hover:brightness-110 transition-all"
      >
        <RefreshCw size={14} />
        Retry
      </button>
    </div>
  </div>
);

// ─── Tab Error Boundary (small, tab-scoped) ──────────────────

interface TabErrorBoundaryProps {
  children: ReactNode;
  onRetry: () => void;
}

interface TabErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class TabErrorBoundary extends Component<TabErrorBoundaryProps, TabErrorBoundaryState> {
  state: TabErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): Partial<TabErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[LazyTab] Error loading tab:', error, info.componentStack);
    // A tab opened after a deploy asks for its old chunk hash, which is gone.
    // Retry can't fix that (React.lazy caches the failed import), so reload
    // once like the app-level boundary does, and don't report the expected
    // case to Sentry (Forgejo #129).
    if (isChunkLoadError(error) && tryAutoReloadForChunkError()) return;
    // This boundary swallows the error, so Sentry's global handlers never see
    // it -- a tab crash used to leave no trace outside the user's console.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
      tags: { boundary: 'lazy-tab' },
    });
  }

  render() {
    if (this.state.hasError) {
      return <TabErrorFallback error={this.state.error} onRetry={this.props.onRetry} />;
    }
    return this.props.children;
  }
}

// ─── LazyTabWrapper (ErrorBoundary + Suspense) ───────────────

export const LazyTabWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [resetKey, setResetKey] = useState(0);

  return (
    <TabErrorBoundary key={resetKey} onRetry={() => setResetKey(k => k + 1)}>
      <Suspense fallback={<TabLoadingFallback />}>
        {children}
      </Suspense>
    </TabErrorBoundary>
  );
};
